<template>
	<page :title="`Client Dashboard - ` + client.name" :breadcrumbs="breadcrumbs">
		<template #actions>
			<v-btn
				:color="client.is_active ? '#7eb440' : '#f44336'"
				class="disable-btn-events"
				large
				min-width="160"
				elevation="0">
				{{ client.is_active ? 'Active' : 'Inactive' }}
			</v-btn>
		</template>
		<v-row>
			<v-col cols="12" md="3">
				<v-sheet rounded>
					<v-card outlined color="#F9F9FB">
						<v-list
							nav
							dense
							class="sidebar-nav">
							<v-list-item-group
								v-model="selectedTab"
								mandatory>
								<v-list-item
									v-for="(item, i) in tabs"
									:key="i"
									active-class="list-item-selected"
									@click="userClickedList(i)">
									<v-list-item-icon>
										<v-icon v-text="item.icon"></v-icon>
									</v-list-item-icon>

									<v-list-item-content>
										<v-list-item-title>
											<div class="d-flex">
												<div class="align-self-center">{{ item.text }}</div>
												<v-chip v-if="item.count" small class="ml-auto">{{ item.count }}</v-chip>
											</div>
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list-item-group>
						</v-list>
					</v-card>
				</v-sheet>

				<v-sheet v-if="client"
					rounded
					class="mt-5">
					<v-card outlined color="#F9F9FB">
						<v-card-title class="align-center text-break">
							Billing Contact
						</v-card-title>
						<v-divider class="thick" />
						<v-list color="#ffffff" dense>
							<v-list-item v-if="client.billing_contact_name">
								<v-list-item-icon>
									<v-icon>
										mdi-account
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<strong>{{ client.billing_contact_name }}</strong>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="client.billing_contact_phone">
								<v-list-item-icon>
									<v-icon>
										mdi-phone
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<strong>{{ client.billing_contact_phone }}</strong>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="client.billing_contact_email">
								<v-list-item-icon>
									<v-icon>
										mdi-email
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<div class="text-break w-100 d-inline-block">
										<a :href="`mailto:` + client.billing_contact_email" class="grey--text text-decoration-none text--darken-2" title="Click to send an email"><strong>{{ client.billing_contact_email }}</strong></a>
									</div>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="client.address1" class="mt-2">
								<v-list-item-icon>
									<v-icon>
										mdi-map-marker
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									{{ client.address1 }}
									<div v-if="client.address2">{{ client.address2 }}</div>
									{{ client.city }}, {{ client.state.name }} {{ client.zipcode }}
								</v-list-item-content>
							</v-list-item>
						</v-list>
					</v-card>
				</v-sheet>
			</v-col>

			<v-col cols="12" md="9" class="project-body">
				<v-tabs v-model="selectedTab" vertical>
					<v-tab-item eager reverse-transition>
						<client-summary
							:client="client"
							@outstanding="loadOutstandingInvoices" />
					</v-tab-item>
					<v-tab-item reverse-transition>
						<v-sheet rounded class="mb-4">
							<v-card color="#125B92" elevation="0" class="rounded-b-0">
								<v-card-title>
									<h3 class="white--text">Edit {{ client.name }}</h3>
								</v-card-title>
								<v-divider />
								<v-card-text class="white pt-8">
									<client-form :client="client" />
								</v-card-text>
							</v-card>
						</v-sheet>
					</v-tab-item>
					<v-tab-item reverse-transition>
						<projects-tab :projects="projects" :active="selectedTab === 2" />
					</v-tab-item>
					<v-tab-item reverse-transition>
						<invoices-tab :invoices="invoices" :active="selectedTab === 3" />
					</v-tab-item>
					<v-tab-item reverse-transition>
						<users-tab :users="users" :active="selectedTab === 4" />
					</v-tab-item>
				</v-tabs>
			</v-col>
		</v-row>
	</page>
</template>
<script>
import Backend from '@/Layouts/Backend'
import Page from '@/Pages/Backend/Components/Page'
import ProjectsTab from '@/Pages/Backend/Clients/Components/Projects'
import InvoicesTab from '@/Pages/Backend/Clients/Components/Invoices'
import UsersTab from '@/Pages/Backend/Clients/Components/Users'
import ClientForm from './Form'
import ClientSummary from '@/Pages/Backend/Clients/Components/ClientSummary.vue'

export default {
	components: {Page, ClientSummary, InvoicesTab, ProjectsTab, UsersTab, ClientForm },
	layout: Backend,
	props: {
		client: {
			type: [Array, Object],
			default: () => {}
		},
		projects: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		users: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		invoices: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		counts: {
			type: Object,
			default: () => {
				return {
					projects: 0,
					invoices: 0,
					users: 0
				}
			},
			required: true
		}
	},
	data () {
		return {
			selectedTab: 0,
			loadedTabs: [],
			tabs: [
				{ icon: 'mdi-bank', text: 'Client Summary' },
				{ icon: 'mdi-pen', text: 'Edit Client' },
				{ icon: 'mdi-briefcase-check', text: 'Projects', count: this.counts.projects },
				{ icon: 'mdi-bank', text: 'Invoices', count: this.counts.invoices },
				{ icon: 'mdi-account-group', text: 'Users', count: this.counts.users }
			],
			breadcrumbs: [
				{ route: 'dashboard', label: 'Dashboard' },
				{ route: 'clients.index', label: 'Clients' },
				{ label: this.client.name }
			]
		}
	},
	watch: {
		selectedTab (tabId) {
			this.$page.props.filters = {}
			this.loadTabId(tabId)
		},
		'counts.projects' (newCount) {
			this.tabs[2].count = newCount
		},
		'counts.invoices' (newCount) {
			this.tabs[3].count = newCount
		},
		'counts.users' (newCount) {
			this.tabs[4].count = newCount
		}
	},
	methods: {
		loadTabId (tabId, data = {}) {
			if (this.loadedTabs.includes(tabId)) {
				return
			}

			const reload = {
				2: ['projects'],
				3: ['invoices'],
				4: ['users']
			}

			if (reload[tabId]) {
				this.$inertia.reload({
					only: [...reload[tabId], ...['counts']],
					data: data,
					onFinish: () => this.loadedTabs.push(tabId)
				})
			}
		},

		userClickedList (index) {
			// User clicked the same tab already open
			// reload the data
			if (index === this.selectedTab) {
				this.loadedTabs.splice(this.loadedTabs.indexOf(index), 1)
				this.$nextTick(() => this.loadTabId(index))
			}
		},

		loadOutstandingInvoices () {
			// Illegal but it works:
			this.$page.props.filters['status'] = 'outstanding'

			this.selectedTab = 3
			this.$nextTick(() => this.loadTabId(3, { status: 'outstanding' }))
		}
	}
}
</script>
